import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { todayIso, fmtDateDMY, syncDateRange } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

const toMoney = (v) => Number(v || 0).toFixed(2);
const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function PmsRateQuotation({ token, selectedPropertyId, selectedProperty, role, permissions }) {
  const { isMobile } = useIsMobile();
  const headers = useCallback(() => {
    const chainId = selectedProperty?.chain_id || '';
    const propertyId = selectedProperty?.id || selectedPropertyId || '';
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(chainId ? { 'x-chain-id': chainId } : {}),
      ...(propertyId ? { 'x-property-id': propertyId } : {})
    };
  }, [token, selectedPropertyId, selectedProperty]);

  const canQuote = role === 'admin' || role === 'hotel_admin' || Boolean(permissions?.hotel_admin) || Boolean(permissions?.pms);

  // ── List state ────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    from_date: addDays(todayIso(), -30),
    to_date: todayIso(),
    status: 'ALL',
    q: ''
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // ── Master data for the form ──────────────────────────────────────────────
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [roomSections, setRoomSections] = useState([defaultSection()]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  function defaultForm() {
    return {
      guest_name: '',
      guest_mobile: '',
      guest_email: '',
      company_name: '',
      customer_gstin: '',
      arrival_date: todayIso(),
      departure_date: addDays(todayIso(), 1),
      adults: 1,
      children: 0,
      validity_days: 14,
      remarks: ''
    };
  }

  function defaultSection() {
    return { room_type_id: '', rate_plan_code: '', rate_per_night: 0, gst_percent: 12, room_count: 1 };
  }

  // All active rate plans — kept unfiltered because they're informational
  // labels on the PDF, not a driver of rate computation.
  const activeRatePlans = useMemo(() =>
    ratePlans.filter((rp) => rp.is_active !== false),
    [ratePlans]
  );

  const loadList = useCallback(async () => {
    setLoading(true); setError(''); setMessage('');
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.set('from_date', filters.from_date);
      if (filters.to_date)   params.set('to_date', filters.to_date);
      if (filters.status && filters.status !== 'ALL') params.set('status', filters.status);
      if (filters.q && filters.q.trim()) params.set('q', filters.q.trim());
      const res = await fetch(`/api/v1/pms/quotations?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load quotations');
      setRows(data.rows || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, headers]);

  const loadMasters = useCallback(async () => {
    try {
      const [rtRes, rpRes] = await Promise.all([
        fetch('/api/v1/pms/room-types', { headers: headers() }),
        fetch('/api/v1/pms/rate-plans', { headers: headers() })
      ]);
      const rt = await rtRes.json();
      const rp = await rpRes.json();
      setRoomTypes(Array.isArray(rt) ? rt : (rt.rows || rt.roomTypes || []));
      setRatePlans(Array.isArray(rp) ? rp : (rp.rows || rp.ratePlans || []));
    } catch (e) { /* non-critical */ }
  }, [headers]);

  useEffect(() => { if (canQuote) { loadList(); loadMasters(); } }, [canQuote, loadList, loadMasters]);

  const openForm = () => {
    setForm(defaultForm());
    setRoomSections([defaultSection()]);
    setPreview(null);
    setError(''); setMessage('');
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setPreview(null); };

  // ── Room section helpers ──────────────────────────────────────────────────
  const addSection = () => setRoomSections(s => [...s, defaultSection()]);
  const removeSection = (i) => setRoomSections(s => s.filter((_, idx) => idx !== i));
  const updateSection = (i, field, value) =>
    setRoomSections(s => s.map((sec, idx) => idx === i ? { ...sec, [field]: value } : sec));

  // Build the rate preview from operator-entered Rate/Night + GST% per section.
  // No backend rate-quote call — rate plan + room type are informational labels
  // on the PDF only.
  const buildPreview = () => {
    setError('');
    try {
      if (!form.arrival_date || !form.departure_date) throw new Error('Set arrival & departure dates');
      if (new Date(form.departure_date) <= new Date(form.arrival_date)) throw new Error('Departure must be after arrival');
      const nights = Math.max(1, Math.round((new Date(form.departure_date) - new Date(form.arrival_date)) / 86400000));

      for (let i = 0; i < roomSections.length; i++) {
        const sec = roomSections[i];
        if (!sec.room_type_id) throw new Error(`Section ${i + 1}: select a Room Type`);
        if (Number(sec.rate_per_night) <= 0) throw new Error(`Section ${i + 1}: enter a rate per night greater than 0`);
      }

      const start = new Date(form.arrival_date);
      const sections = roomSections.map((sec) => {
        const ratePerNight = Number(sec.rate_per_night || 0);
        const gstPct = Math.max(0, Number(sec.gst_percent || 0));
        const rc = Math.max(1, Number(sec.room_count || 1));
        const subtotal = Number((ratePerNight * nights * rc).toFixed(2));
        const cgstPct = gstPct / 2;
        const sgstPct = gstPct / 2;
        const cgstAmt = Number((subtotal * cgstPct / 100).toFixed(2));
        const sgstAmt = Number((subtotal * sgstPct / 100).toFixed(2));
        const taxTotal = Number((cgstAmt + sgstAmt).toFixed(2));
        const sectionTotal = Number((subtotal + taxTotal).toFixed(2));

        const ratePlanObj = sec.rate_plan_code
          ? activeRatePlans.find((rp) => rp.rate_plan_code === sec.rate_plan_code)
          : null;
        const ratePlanLabel = ratePlanObj?.rate_plan_name || sec.rate_plan_code || 'Tariff';
        const ratePlanId = ratePlanObj?.rate_plan_id || null;

        const items = [];
        for (let i = 0; i < nights; i++) {
          const d = new Date(start); d.setDate(d.getDate() + i);
          items.push({
            date: d.toISOString().slice(0, 10),
            day: d.toLocaleDateString('en-IN', { weekday: 'long' }),
            rate: Number((ratePerNight * rc).toFixed(2)),
            rate_plan_label: ratePlanLabel
          });
        }

        return {
          room_type_id: sec.room_type_id,
          room_type_name: roomTypes.find((rt) => Number(rt.room_type_id) === Number(sec.room_type_id))?.room_type_name || '',
          rate_plan_id: ratePlanId,
          rate_plan_code: sec.rate_plan_code,
          rate_plan_name: ratePlanLabel,
          rate_per_night: ratePerNight,
          gst_percent: gstPct,
          room_count: rc,
          nights,
          subtotal,
          cgst_percent: cgstPct,
          sgst_percent: sgstPct,
          igst_percent: 0,
          cgst_amount: cgstAmt,
          sgst_amount: sgstAmt,
          igst_amount: 0,
          tax_total: taxTotal,
          section_total: sectionTotal,
          line_items: items
        };
      });

      const combined = sections.reduce((acc, sec) => ({
        subtotal:     Number((acc.subtotal     + sec.subtotal).toFixed(2)),
        cgst_amount:  Number((acc.cgst_amount  + sec.cgst_amount).toFixed(2)),
        sgst_amount:  Number((acc.sgst_amount  + sec.sgst_amount).toFixed(2)),
        igst_amount:  0,
        tax_total:    Number((acc.tax_total    + sec.tax_total).toFixed(2)),
        grand_total:  Number((acc.grand_total  + sec.section_total).toFixed(2))
      }), { subtotal: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, tax_total: 0, grand_total: 0 });

      setPreview({ nights, sections, combined });
    } catch (e) { setError(e.message); }
  };

  const saveQuotation = async () => {
    if (!preview) { setError('Please Calculate Rate first.'); return; }
    if (!form.guest_name.trim()) { setError('Guest Name is required'); return; }
    setSaving(true); setError('');
    try {
      const first = preview.sections[0];
      const body = {
        ...form,
        // Primary fields from first section (backward compat with convert-to-reservation)
        room_type_id:   first.room_type_id,
        room_type_name: first.room_type_name,
        rate_plan_id:   first.rate_plan_id,
        rate_plan_name: first.rate_plan_name,
        meal_plan:      null,
        room_count:     first.room_count,
        nights:         preview.nights,
        // Combined totals across all sections
        subtotal:       preview.combined.subtotal,
        tax_total:      preview.combined.tax_total,
        cgst_amount:    preview.combined.cgst_amount,
        sgst_amount:    preview.combined.sgst_amount,
        igst_amount:    preview.combined.igst_amount,
        cgst_percent:   first.cgst_percent,
        sgst_percent:   first.sgst_percent,
        igst_percent:   0,
        grand_total:    preview.combined.grand_total,
        // First section's line_items for backward compat
        line_items:     first.line_items,
        // All sections for multi-section PDF rendering
        room_sections:  preview.sections
      };
      const res = await fetch('/api/v1/pms/quotations', {
        method: 'POST', headers: headers(), body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setMessage(`Quotation ${data.quotation.quotation_number} created.`);
      setShowForm(false);
      loadList();
      openPdf(data.quotation.quotation_id);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const openPdf = async (quotationId) => {
    try {
      const res = await fetch(`/api/v1/pms/quotations/${quotationId}/pdf`, { headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `PDF failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError(e.message); }
  };

  const emailQuotation = async (row) => {
    const to = window.prompt('Email this quotation to:', row.contact_email || row.email || '');
    if (!to || !to.includes('@')) return;
    try {
      const res = await fetch(`/api/v1/pms/quotations/${row.quotation_id}/email`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ to }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Email failed');
      setError('');
      alert(`Quotation emailed to ${data.to}`);
    } catch (e) { setError(e.message); }
  };

  const convertToReservation = async (row) => {
    if (!window.confirm(`Convert ${row.quotation_number} to a TENTATIVE reservation?`)) return;
    try {
      const res = await fetch(`/api/v1/pms/quotations/${row.quotation_id}/convert`, {
        method: 'POST', headers: headers()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Convert failed');
      setMessage(`Reservation ${data.reservation_no} created from quotation ${row.quotation_number}.`);
      loadList();
    } catch (e) { setError(e.message); }
  };

  const cancelQuote = async (row) => {
    if (!window.confirm(`Cancel quotation ${row.quotation_number}?`)) return;
    try {
      const res = await fetch(`/api/v1/pms/quotations/${row.quotation_id}/cancel`, {
        method: 'POST', headers: headers()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Cancel failed');
      setMessage(`Quotation ${row.quotation_number} cancelled.`);
      loadList();
    } catch (e) { setError(e.message); }
  };

  const statusBadge = (s) => {
    const palettes = {
      ACTIVE:    { bg: '#dcfce7', color: '#166534' },
      EXPIRED:   { bg: '#fef9c3', color: '#854d0e' },
      CONVERTED: { bg: '#dbeafe', color: '#1e40af' },
      CANCELLED: { bg: '#fee2e2', color: '#991b1b' }
    };
    const p = palettes[s] || { bg: '#e5e7eb', color: '#374151' };
    return (
      <span style={{ background: p.bg, color: p.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{s}</span>
    );
  };

  if (!canQuote) return <div style={{ padding: 24, color: '#b91c1c' }}>You do not have permission to access Rate Quotations.</div>;

  return (
    <div style={{ padding: '1rem 1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0F2C4D' }}>Rate Quotations</h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: 13, color: '#718096' }}>
            Generate branded rate quotations for prospective guests. Each quote has a validity window and can be converted to a TENTATIVE reservation.
          </p>
        </div>
        <button onClick={openForm} style={btnPrimary}>+ New Quotation</button>
      </div>

      {/* Filter bar */}
      <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem', display: 'flex', gap: '0.8rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div><label style={lbl}>From</label><input type="date" value={filters.from_date} onChange={(e) => setFilters(syncDateRange({ ...filters, from_date: e.target.value }, 'from_date', 'to_date', 'from_date'))}style={inp} /></div>
        <div><label style={lbl}>To</label><input type="date" value={filters.to_date} min={filters.from_date || undefined} onChange={(e) => setFilters(syncDateRange({ ...filters, to_date: e.target.value }, 'from_date', 'to_date', 'to_date'))} style={inp} /></div>
        <div><label style={lbl}>Status</label>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={inp}>
            <option value="ALL">All</option>
            <option value="ACTIVE">Active</option>
            <option value="EXPIRED">Expired</option>
            <option value="CONVERTED">Converted</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}><label style={lbl}>Search</label>
          <input type="text" placeholder="Quote no, guest name, mobile..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} style={inp} />
        </div>
        <button onClick={loadList} style={btnSecondary}>Search</button>
      </div>

      {error && <div style={alertError}>{error}</div>}
      {message && <div style={alertOk}>{message}</div>}

      {/* List */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0F2C4D', color: '#fff' }}>
              <th style={th}>Quote No</th>
              <th style={th}>Guest</th>
              <th style={th}>Stay</th>
              <th style={th}>Room</th>
              <th style={th}>Rate Plan</th>
              <th style={{ ...th, textAlign: 'right' }}>Grand Total</th>
              <th style={th}>Issue → Valid</th>
              <th style={th}>Status</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: '1rem', textAlign: 'center', color: '#718096' }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} style={{ padding: '1.5rem', textAlign: 'center', color: '#718096' }}>No quotations found.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.quotation_id} style={{ borderTop: '1px solid #edf2f7' }}>
                <td style={td}><strong style={{ color: '#0F2C4D' }}>{r.quotation_number}</strong></td>
                <td style={td}>
                  {r.guest_name}<br/>
                  <small style={{ color: '#718096' }}>{r.guest_mobile || ''}{r.company_name ? ' · ' + r.company_name : ''}</small>
                </td>
                <td style={td}>{fmtDateDMY(r.arrival_date)} → {fmtDateDMY(r.departure_date)}<br/><small style={{ color: '#718096' }}>{r.nights} night{r.nights > 1 ? 's' : ''}</small></td>
                <td style={td}>{r.room_count}× {r.room_type_name || '-'}</td>
                <td style={td}>{r.rate_plan_name || '-'}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>₹{toMoney(r.grand_total)}</td>
                <td style={td}>{fmtDateDMY(r.issue_date)} → {fmtDateDMY(r.valid_until)}</td>
                <td style={td}>{statusBadge(r.status)}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openPdf(r.quotation_id)} title="View / Print PDF" style={btnSmall}>PDF</button>
                    <button onClick={() => emailQuotation(r)} title="Email quotation PDF" style={btnSmall}>Email</button>
                    {r.status === 'ACTIVE' && (
                      <>
                        <button onClick={() => convertToReservation(r)} title="Convert to TENTATIVE reservation" style={{ ...btnSmall, background: '#dbeafe', color: '#1e40af' }}>Convert</button>
                        <button onClick={() => cancelQuote(r)} title="Cancel quotation" style={{ ...btnSmall, background: '#fee2e2', color: '#991b1b' }}>Cancel</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── New Quotation modal ── */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ width: 'min(960px,97vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.25rem', position: 'relative' }}>
            <button onClick={closeForm} style={{ position: 'absolute', top: 8, right: 12, background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#718096' }}>×</button>
            <h2 style={{ margin: '0 0 0.8rem', color: '#0F2C4D' }}>New Rate Quotation</h2>

            {/* Customer Details */}
            <div style={sectionHdr}>Customer Details</div>
            <div style={grid2}>
              <div><label style={lbl}>Guest Name *</label><input type="text" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Mobile</label><input type="text" value={form.guest_mobile} onChange={(e) => setForm({ ...form, guest_mobile: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Email</label><input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Company</label><input type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Customer GSTIN (optional)</label><input type="text" maxLength={15} value={form.customer_gstin} onChange={(e) => setForm({ ...form, customer_gstin: e.target.value.toUpperCase() })} style={inp} /></div>
            </div>

            {/* Stay Details */}
            <div style={sectionHdr}>Stay Details</div>
            <div style={isMobile ? { ...grid3, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : grid3}>
              <div><label style={lbl}>Check-in</label><input type="date" value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Check-out</label><input type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} style={inp} /></div>
              <div><label style={lbl}>Validity (days)</label><input type="number" min="1" max="365" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: Number(e.target.value) })} style={inp} /></div>
              <div><label style={lbl}>Adults</label><input type="number" min="1" value={form.adults} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} style={inp} /></div>
              <div><label style={lbl}>Children</label><input type="number" min="0" value={form.children} onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} style={inp} /></div>
              <div><label style={lbl}>Remarks (optional)</label><input type="text" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} style={inp} /></div>
            </div>

            {/* Room Types — dynamic multi-section rows */}
            <div style={sectionHdr}>Room Type(s) &amp; Rates</div>

            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 2fr 1.3fr 0.8fr 0.8fr 32px', gap: '0.45rem', minWidth: isMobile ? 560 : undefined, padding: '0 0.6rem', marginBottom: 4 }}>
              {['Room Type *', 'Rate Plan (optional)', 'Rate / Night (₹) *', 'GST %', '# Rooms', ''].map((h, i) => (
                <div key={i} style={lbl}>{h}</div>
              ))}
            </div>

            {roomSections.map((sec, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2.5fr 2fr 1.3fr 0.8fr 0.8fr 32px', gap: '0.45rem', minWidth: isMobile ? 560 : undefined, alignItems: 'center', background: '#f7fafc', padding: '0.5rem 0.6rem', borderRadius: 5, border: '1px solid #e2e8f0', marginBottom: 6 }}>
                <select value={sec.room_type_id} onChange={(e) => { updateSection(i, 'room_type_id', e.target.value); setPreview(null); }} style={inp}>
                  <option value="">-- Select --</option>
                  {roomTypes.map((rt) => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>)}
                </select>
                <select value={sec.rate_plan_code} onChange={(e) => { updateSection(i, 'rate_plan_code', e.target.value); setPreview(null); }} style={inp}>
                  <option value="">-- None / Tariff --</option>
                  {activeRatePlans.map((rp) => <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} — {rp.rate_plan_name}</option>)}
                </select>
                <input type="number" min="0" step="0.01" value={sec.rate_per_night} onChange={(e) => { updateSection(i, 'rate_per_night', Number(e.target.value)); setPreview(null); }} style={inp} />
                <input type="number" min="0" max="28" step="0.01" value={sec.gst_percent} onChange={(e) => { updateSection(i, 'gst_percent', Number(e.target.value)); setPreview(null); }} style={inp} />
                <input type="number" min="1" value={sec.room_count} onChange={(e) => { updateSection(i, 'room_count', Number(e.target.value)); setPreview(null); }} style={inp} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {roomSections.length > 1 ? (
                    <button onClick={() => { removeSection(i); setPreview(null); }} title="Remove this room type" style={{ width: 28, height: 28, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>×</button>
                  ) : <div />}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '0.7rem', marginTop: 6, alignItems: 'center' }}>
              <button onClick={() => { addSection(); setPreview(null); }} style={{ ...btnSecondary, fontSize: 12 }}>+ Add Room Type</button>
              <button onClick={buildPreview} style={{ ...btnSecondary, background: '#0F2C4D', color: '#fff' }}>Calculate Rate</button>
              <small style={{ color: '#718096', fontSize: 11, marginLeft: 'auto' }}>
                Rate × nights × rooms — intra-state 50/50 CGST/SGST split.
              </small>
            </div>

            {/* Rate Preview */}
            {preview && (
              <div style={{ marginTop: '1rem', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.9rem' }}>
                <div style={{ fontWeight: 700, color: '#0F2C4D', marginBottom: 10 }}>
                  Rate Preview — {preview.nights} night{preview.nights > 1 ? 's' : ''}
                  {preview.sections.length > 1 ? ` · ${preview.sections.length} room types` : ` × ${preview.sections[0].room_count} room${preview.sections[0].room_count > 1 ? 's' : ''}`}
                </div>

                {preview.sections.map((sec, si) => (
                  <div key={si} style={{ marginBottom: preview.sections.length > 1 ? 16 : 0 }}>
                    {preview.sections.length > 1 && (
                      <div style={{ fontWeight: 600, color: '#0F2C4D', fontSize: 12, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        {sec.room_type_name} — {sec.room_count} Room{sec.room_count > 1 ? 's' : ''}
                        {sec.rate_plan_name && sec.rate_plan_name !== 'Tariff' ? ` · ${sec.rate_plan_name}` : ''}
                      </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#0F2C4D', color: '#fff' }}>
                          <th style={th2}>Date</th><th style={th2}>Day</th><th style={th2}>Rate Plan</th><th style={{ ...th2, textAlign: 'right' }}>Rate (INR)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sec.line_items.map((li, j) => (
                          <tr key={j} style={{ borderTop: '1px solid #edf2f7' }}>
                            <td style={td2}>{fmtDateDMY(li.date)}</td>
                            <td style={td2}>{li.day}</td>
                            <td style={td2}>{li.rate_plan_label}</td>
                            <td style={{ ...td2, textAlign: 'right' }}>{toMoney(li.rate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Per-section subtotals (only shown for multi-section) */}
                    {preview.sections.length > 1 && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 4, marginTop: 6, maxWidth: 260, marginLeft: 'auto', fontSize: 12, color: '#4a5568' }}>
                        <div>Subtotal ({preview.nights}N × {sec.room_count}R)</div><div style={{ textAlign: 'right' }}>{toMoney(sec.subtotal)}</div>
                        {sec.cgst_amount > 0 && (<><div>CGST @ {sec.cgst_percent}%</div><div style={{ textAlign: 'right' }}>{toMoney(sec.cgst_amount)}</div></>)}
                        {sec.sgst_amount > 0 && (<><div>SGST @ {sec.sgst_percent}%</div><div style={{ textAlign: 'right' }}>{toMoney(sec.sgst_amount)}</div></>)}
                        <div style={{ fontWeight: 600, borderTop: '1px solid #cbd5e0', paddingTop: 4, marginTop: 2 }}>Section Total</div>
                        <div style={{ fontWeight: 600, textAlign: 'right', borderTop: '1px solid #cbd5e0', paddingTop: 4, marginTop: 2 }}>{toMoney(sec.section_total)}</div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Combined grand total */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, marginTop: preview.sections.length > 1 ? 12 : 12, maxWidth: 320, marginLeft: 'auto', fontSize: 13, borderTop: preview.sections.length > 1 ? '1px dashed #cbd5e0' : 'none', paddingTop: preview.sections.length > 1 ? 10 : 0 }}>
                  {preview.sections.length === 1 && (
                    <><div>Room Subtotal ({preview.nights} night{preview.nights > 1 ? 's' : ''})</div><div style={{ textAlign: 'right' }}>{toMoney(preview.combined.subtotal)}</div></>
                  )}
                  {preview.sections.length > 1 && (
                    <><div style={{ fontWeight: 600 }}>Combined Subtotal</div><div style={{ fontWeight: 600, textAlign: 'right' }}>{toMoney(preview.combined.subtotal)}</div></>
                  )}
                  {preview.combined.cgst_amount > 0 && (<><div>CGST</div><div style={{ textAlign: 'right' }}>{toMoney(preview.combined.cgst_amount)}</div></>)}
                  {preview.combined.sgst_amount > 0 && (<><div>SGST</div><div style={{ textAlign: 'right' }}>{toMoney(preview.combined.sgst_amount)}</div></>)}
                  {preview.combined.igst_amount > 0 && (<><div>IGST</div><div style={{ textAlign: 'right' }}>{toMoney(preview.combined.igst_amount)}</div></>)}
                  <div style={{ fontWeight: 700, color: '#0F2C4D', borderTop: '2px solid #0F2C4D', paddingTop: 6 }}>Grand Total (INR)</div>
                  <div style={{ fontWeight: 700, color: '#0F2C4D', borderTop: '2px solid #0F2C4D', paddingTop: 6, textAlign: 'right' }}>{toMoney(preview.combined.grand_total)}</div>
                </div>
              </div>
            )}

            {error && <div style={{ ...alertError, marginTop: 10 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={closeForm} style={btnSecondary}>Cancel</button>
              <button onClick={saveQuotation} disabled={!preview || saving} style={btnPrimary}>
                {saving ? 'Saving…' : 'Save & Generate PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#4a5568', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
const inp = { width: '100%', padding: '0.42rem 0.6rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };
const sectionHdr = { fontWeight: 700, color: '#0F2C4D', fontSize: 13, margin: '14px 0 8px', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.7rem' };
const th = { padding: '0.5rem 0.8rem', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left' };
const td = { padding: '0.55rem 0.8rem', fontSize: 13, color: '#2d3748', verticalAlign: 'top' };
const th2 = { padding: '6px 10px', fontSize: 11, fontWeight: 700, textAlign: 'left' };
const td2 = { padding: '6px 10px', fontSize: 12 };
const btnPrimary = { padding: '0.5rem 1rem', background: '#D4AF37', color: '#0F2C4D', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 700 };
const btnSecondary = { padding: '0.5rem 1rem', background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 };
const btnSmall = { padding: '3px 8px', background: '#f7fafc', color: '#0F2C4D', border: '1px solid #cbd5e0', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const alertError = { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '0.6rem 0.9rem', color: '#b91c1c', fontSize: 13, margin: '10px 0' };
const alertOk = { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.6rem 0.9rem', color: '#166534', fontSize: 13, margin: '10px 0' };
